// Residual stock helpers for the POS catalog UI.
// Catalog `stock_disponible` is a snapshot; the cart must reduce what the
// cashier sees as available, and a completed sale must burn that stock from
// the in-memory (and cached) catalog until the next refresh.

export function residualStock(stockDisponible: number | null, cartQty: number): number | null {
  if (stockDisponible === null) return null;
  const qty = Number.isFinite(cartQty) ? Math.max(0, cartQty) : 0;
  return Math.max(0, stockDisponible - qty);
}

export function isCatalogAgotado(stockDisponible: number | null): boolean {
  return stockDisponible !== null && stockDisponible <= 0;
}

export function isMaxReached(stockDisponible: number | null, cartQty: number): boolean {
  if (stockDisponible === null) return false;
  return cartQty >= stockDisponible && stockDisponible > 0;
}

export type CatalogStockKind = 'vendible' | 'agotado' | 'sin_inventario' | 'sin_dato';

/**
 * `inventario_asignado === false` means this branch has no stock row.
 * A legacy snapshot (flag missing) with quantity 0 is treated the same way:
 * the cashier needs to assign it before the POS can sell it.
 * Quantity 0 with the flag true is a real stock-out.
 */
export function catalogStockKind(item: {
  stock_disponible: number | null;
  inventario_asignado?: boolean | null;
}): CatalogStockKind {
  if (item.inventario_asignado === false) return 'sin_inventario';
  if (item.stock_disponible === null && item.inventario_asignado == null) return 'sin_dato';
  const stock = item.stock_disponible ?? 0;
  if (stock <= 0) return item.inventario_asignado === true ? 'agotado' : 'sin_inventario';
  return 'vendible';
}

export function summarizeCatalogStock(
  items: Array<{ stock_disponible: number | null; inventario_asignado?: boolean | null }>,
): { vendibles: number; agotados: number; sinInventario: number } {
  const summary = { vendibles: 0, agotados: 0, sinInventario: 0 };
  for (const item of items) {
    const kind = catalogStockKind(item);
    if (kind === 'vendible') summary.vendibles += 1;
    else if (kind === 'agotado') summary.agotados += 1;
    else if (kind === 'sin_inventario') summary.sinInventario += 1;
  }
  return summary;
}

export function catalogStockSummaryLabel(summary: {
  vendibles: number;
  agotados: number;
  sinInventario: number;
}): string {
  const parts = [
    summary.vendibles === 1 ? '1 se puede vender' : `${summary.vendibles} se pueden vender`,
  ];
  if (summary.sinInventario === 1) parts.push('1 sin inventario en esta bodega');
  else if (summary.sinInventario > 1) {
    parts.push(`${summary.sinInventario} sin inventario en esta bodega`);
  }
  if (summary.agotados === 1) parts.push('1 agotado');
  else if (summary.agotados > 1) parts.push(`${summary.agotados} agotados`);
  return parts.join(' · ');
}

/**
 * Products the cashier of this branch is allowed to see.
 * A missing stock row means the admin never assigned the product to the
 * branch, so it stays out of the grid, the search, and the category chips.
 * A real stock-out (row exists, quantity 0) stays visible as agotado.
 * `sin_dato` stays visible: the snapshot was loaded without a branch.
 */
export function catalogForCashier<
  T extends { stock_disponible: number | null; inventario_asignado?: boolean | null },
>(items: T[]): T[] {
  return items.filter((item) => catalogStockKind(item) !== 'sin_inventario');
}

export function blockedSaleMessage(nombre: string, kind: CatalogStockKind): string | null {
  if (kind === 'sin_inventario') {
    return `${nombre} no tiene inventario en esta bodega. Asignalo en Admin, Productos, en la bodega de esta sucursal.`;
  }
  if (kind === 'agotado') return `${nombre} está agotado en esta bodega.`;
  return null;
}

/**
 * Applies sold cart quantities to the catalog snapshot (post-checkout).
 * Presentaciones not in the cart are left untouched; null stock stays null.
 */
export function applyCartToCatalogStock<
  T extends { presentacion_id: string; stock_disponible: number | null },
>(items: T[], cart: Array<{ presentacionId: string; cantidad: number }>): T[] {
  if (cart.length === 0) return items;
  const soldByPresentacion = new Map<string, number>();
  for (const line of cart) {
    const prev = soldByPresentacion.get(line.presentacionId) ?? 0;
    soldByPresentacion.set(line.presentacionId, prev + line.cantidad);
  }
  return items.map((item) => {
    const sold = soldByPresentacion.get(item.presentacion_id);
    if (!sold || item.stock_disponible === null) return item;
    return {
      ...item,
      stock_disponible: Math.max(0, item.stock_disponible - sold),
    };
  });
}
